import { toCsv, type CsvCell, downloadTextFile } from '@/utils/csv/csv'

type Primitive = string | number | boolean | null | undefined | Date

export type ObjectToCsvOptions<T extends Record<string, unknown>> = {
  flatten?: boolean
  delimiter?: string
  pick?: string[]
  omit?: string[]
  headerMap?: Partial<Record<string, string>>
  valuePathMap?: Record<string, string>
  formatValue?: (keyPath: string, value: unknown, row: T) => Primitive
  getCell?: (key: string, row: T) => unknown
  bom?: boolean
  keyOrder?: string[]
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    v != null &&
    typeof v === 'object' &&
    !(v instanceof Date) &&
    !Array.isArray(v)
  )
}

function flattenObject(
  obj: Record<string, unknown>,
  delimiter = '.',
  prefix = ''
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    const keyPath = prefix ? `${prefix}${delimiter}${k}` : k
    if (isPlainObject(v)) {
      Object.assign(out, flattenObject(v, delimiter, keyPath))
    } else {
      out[keyPath] = v
    }
  }
  return out
}

function collectKeyPaths(
  rows: ReadonlyArray<Record<string, unknown>>
): string[] {
  const set = new Set<string>()
  for (const r of rows) {
    for (const k of Object.keys(r)) set.add(k)
  }
  return Array.from(set).sort()
}

function getByPath(obj: unknown, path: string, delimiter = '.'): unknown {
  const keys = path.split(delimiter)
  let cur: unknown = obj
  for (const key of keys) {
    if (!isPlainObject(cur)) return undefined
    cur = cur[key]
  }
  return cur
}

function toRecord(row: unknown): Record<string, unknown> {
  return isPlainObject(row) ? row : {}
}

export function objectsToCsvMatrix<T extends Record<string, unknown>>(
  rows: ReadonlyArray<T>,
  options: ObjectToCsvOptions<T> = {}
): CsvCell[][] {
  const {
    flatten = true,
    delimiter = '.',
    pick,
    omit,
    headerMap,
    formatValue,
    getCell,
    valuePathMap,
    keyOrder
  } = options

  // 1) フラット化（or そのまま）
  const flatRows: Record<string, unknown>[] = flatten
    ? rows.map((r) => flattenObject(toRecord(r), delimiter))
    : rows.map((r) => toRecord(r))

  // 2) ヘッダのキー列決定
  let keyPaths = pick
    ? [...pick]
    : keyOrder
      ? [...keyOrder]
      : collectKeyPaths(flatRows)

  if (omit?.length) {
    const omitSet = new Set(omit)
    keyPaths = keyPaths.filter((k) => !omitSet.has(k))
  }

  // 3) ヘッダ
  const header: CsvCell[] = keyPaths.map((k) => headerMap?.[k] ?? k)

  // 4) ボディ
  const body: CsvCell[][] = rows.map((originalRow, idx) => {
    const flat = flatRows[idx]
    return keyPaths.map((k) => {
      if (getCell) {
        const v = getCell(k, originalRow)
        return (formatValue ? formatValue(k, v, originalRow) : v) as CsvCell
      }

      const path = valuePathMap?.[k] ?? k
      const raw = flatten ? flat[path] : getByPath(originalRow, path, delimiter)
      const v = formatValue ? formatValue(k, raw, originalRow) : raw
      return v as CsvCell
    })
  })

  return [header, ...body]
}

export function downloadObjectsAsCsv<T extends Record<string, unknown>>(
  filename: string,
  rows: ReadonlyArray<T>,
  options: ObjectToCsvOptions<T> = {}
) {
  const matrix = objectsToCsvMatrix(rows, options)
  const csv = toCsv(matrix)
  const text = options.bom ? '\ufeff' + csv : csv
  downloadTextFile(filename, text)
}
